// Global Instructions Rule Applied!
// Field configs of the Commitment forms, one function per FormSection. The
// forms hold a draft and save explicitly (Create / Save), so nothing here
// auto-patches; keys are the form-local CommitmentFieldKeys.

import { FieldConfig, FieldTypeEnum, SelectTypeEnum } from '../../../core/components/InputManager/types.ts'
import { SelectListEntry } from '../../../state_manager/data_models/select_list_entry.ts'
import { decimalToPercent } from '../../../core/utils/inputNumberFormat.ts'
import { effectiveFundCarryDefaults, type FundCarryDefaults } from '../../../core/utils/fundCarryDefaults.ts'
import {
  CommitmentFieldKeys,
  type CommitmentCoreDraft,
  type CommitmentNoticeDraft,
  type CommitmentTermsDraft,
  type NewInvestorDraft
} from './commitmentDrafts.ts'

const DATE_FORMAT = 'MM/DD/YYYY'

export const DATE_CLOSED_HELP =
  'The date the commitment closed into the fund. It decides which capital calls include the commitment and ' +
  'whether it is a late closer.'
export const GP_COMMITMENT_HELP = "The general partner's own commitment. Printed on the ILPA workbook."
export const GP_CONSOLIDATED_HELP = `${GP_COMMITMENT_HELP} Sets the primary and satellite commitments.`
export const CARRY_RECIPIENT_HELP =
  "The row the fund's carried interest is allocated and paid to. It may be a commitment of 0 - the carry's own " +
  "account, apart from the general partner's own commitment. A fund that marks none pays the carry to its GP " +
  'commitment.'
export const CARRY_CONSOLIDATED_HELP =
  `${CARRY_RECIPIENT_HELP} Sets the commitment in every member fund: each fund's carried interest goes to its ` +
  'pocket. A commitment of 0 gets a pocket of 0 in every member fund.'
export const SBIC_INVESTOR_TYPE_HELP =
  'SBA capital treatment for Regulatory Capital (13 CFR 107.230): institutional commitments count in full, ' +
  'non-institutional investors count paid-in capital only, State/local government money is capped at 33% in ' +
  'aggregate.'
export const NEW_INVESTOR_VALUE = '__new_investor__'

interface CommitmentFieldOptions {
  statusOptions: SelectListEntry[]
  currencySymbol?: string | null
  /** read-only: a pocket's amount, status and dates are the consolidated commitment's */
  readOnly?: boolean
  gpHelp?: string
  amountLabel?: string
}

/**
 * Commitment section: amount, status, the two dates and the GP marker. On a
 * pocket everything but the GP marker is read-only, so the two are split:
 * see getGpField. A standalone commitment adds the carry marker after it:
 * see getCarryRecipientField.
 */
export function getCommitmentFields(
  core: CommitmentCoreDraft,
  { statusOptions, currencySymbol, readOnly = false, amountLabel = 'Commitment' }: CommitmentFieldOptions
): FieldConfig[] {
  const editable = !readOnly
  return [
    {
      key: CommitmentFieldKeys.amount,
      label: amountLabel,
      type: FieldTypeEnum.CURRENCY,
      value: core.amount,
      currencySymbol: currencySymbol ?? undefined,
      min: 0,
      required: editable,
      editable
    },
    {
      key: CommitmentFieldKeys.status_id,
      label: 'Status',
      type: FieldTypeEnum.SELECT,
      value: core.status_id,
      options: statusOptions,
      selectType: SelectTypeEnum.FILTER,
      required: editable,
      editable
    },
    {
      key: CommitmentFieldKeys.date_committed,
      label: 'Date Committed',
      type: FieldTypeEnum.DATE,
      value: core.date_committed,
      format: DATE_FORMAT,
      allowClear: true,
      editable
    },
    {
      key: CommitmentFieldKeys.date_closed,
      label: 'Date Closed',
      type: FieldTypeEnum.DATE,
      value: core.date_closed,
      format: DATE_FORMAT,
      allowClear: true,
      editable,
      labelTooltip: DATE_CLOSED_HELP
    }
  ]
}

export function getGpField(core: CommitmentCoreDraft, help: string = GP_COMMITMENT_HELP): FieldConfig[] {
  return [
    {
      key: CommitmentFieldKeys.is_gp_commitment,
      label: 'GP commitment',
      type: FieldTypeEnum.SWITCH,
      value: core.is_gp_commitment,
      editable: true,
      labelTooltip: help
    }
  ]
}

/** The carry marker, right after the GP marker: on a standalone commitment,
 *  on a pocket (owner 2026-10-12) and on a consolidated commitment (phase 5
 *  part 1, which sets every pocket). */
export function getCarryRecipientField(core: CommitmentCoreDraft, help: string = CARRY_RECIPIENT_HELP): FieldConfig[] {
  return [
    {
      key: CommitmentFieldKeys.is_carry_recipient,
      label: 'Receives carried interest',
      type: FieldTypeEnum.SWITCH,
      value: core.is_carry_recipient,
      editable: true,
      labelTooltip: help
    }
  ]
}

function rateField(key: string, label: string, value: number | null, editable: boolean, extra: Partial<FieldConfig> = {}): FieldConfig {
  return {
    key,
    label,
    type: FieldTypeEnum.NUMBER,
    value,
    displayValue: value == null ? undefined : `${value}%`,
    min: 0,
    max: 100,
    editable,
    ...extra
  } as FieldConfig
}

function multipleField(key: string, label: string, value: number | null, editable: boolean, extra: Partial<FieldConfig> = {}): FieldConfig {
  return {
    key,
    label,
    type: FieldTypeEnum.NUMBER,
    value,
    displayValue: value == null ? undefined : `${value}x`,
    min: 0,
    editable,
    ...extra
  } as FieldConfig
}

/** "Fund: 20%" / "Fund: 1x" - the figure a blank term inherits; no placeholder where nothing states one. */
const fundPlaceholder = (value: number | null | undefined, suffix: '%' | 'x', toPct = false, who = 'Fund'): string | undefined =>
  value == null ? undefined : `${who}: ${toPct ? decimalToPercent(value) : value}${suffix}`

const INHERIT_HELP =
  "Leave all four blank to use the fund's terms (the Carried Interest section of the fund's Terms tab). A side letter is the full set: Carry (Base) % and Hurdle (Base) x at least, and the stepped pair together."

/**
 * Terms section. Rates are typed as percentages. A blank management fee means
 * the fund's rate. The carry terms are all blank (the fund's Carried Interest
 * terms apply) or the commitment's own full set (wf__lp_terms: the row with a
 * base rate resolves as a whole - a blank hurdle is 1x, blank stepped figures
 * are no step). The placeholders say which: the fund's figures while the row
 * states none, the row's own fallbacks once it states a base rate.
 */
export function getTermsFields(
  terms: CommitmentTermsDraft,
  {
    fundFeeRatePct,
    fundDefaults = null,
    readOnly = false
  }: { fundFeeRatePct: number | null; fundDefaults?: FundCarryDefaults | null; readOnly?: boolean }
): FieldConfig[] {
  const editable = !readOnly
  const own = terms.base_carry_rate != null
  const d = fundDefaults ? effectiveFundCarryDefaults(fundDefaults) : null
  // the row's own fallbacks (percent-typed, as the draft is)
  const ownHurdle = terms.base_hurdle_multiple ?? 1
  const placeholders = own
    ? {
        hurdle: 'Base: 1x',
        steppedRate: `No step: ${terms.base_carry_rate}%`,
        steppedHurdle: `No step: ${ownHurdle}x`
      }
    : {
        hurdle: fundPlaceholder(d?.base_hurdle_multiple, 'x'),
        steppedRate: fundPlaceholder(d?.stepped_carry_rate, '%', true),
        steppedHurdle: fundPlaceholder(d?.stepped_hurdle_multiple, 'x')
      }
  return [
    rateField(CommitmentFieldKeys.management_fee_rate, 'Management Fee %', terms.management_fee_rate, editable, {
      placeholder: fundFeeRatePct != null ? `Fund: ${fundFeeRatePct}%` : undefined,
      labelTooltip: "Leave blank to use the fund's rate."
    }),
    rateField(CommitmentFieldKeys.base_carry_rate, 'Carry (Base) %', terms.base_carry_rate, editable, {
      placeholder: fundPlaceholder(d?.base_carry_rate, '%', true),
      labelTooltip: INHERIT_HELP
    }),
    multipleField(CommitmentFieldKeys.base_hurdle_multiple, 'Hurdle (Base) x', terms.base_hurdle_multiple, editable, {
      placeholder: placeholders.hurdle,
      labelTooltip: INHERIT_HELP
    }),
    rateField(CommitmentFieldKeys.stepped_carry_rate, 'Carry (Stepped) %', terms.stepped_carry_rate, editable, {
      placeholder: placeholders.steppedRate,
      labelTooltip: INHERIT_HELP
    }),
    multipleField(
      CommitmentFieldKeys.stepped_hurdle_multiple,
      'Hurdle (Stepped) x',
      terms.stepped_hurdle_multiple,
      editable,
      { placeholder: placeholders.steppedHurdle, labelTooltip: INHERIT_HELP }
    )
  ]
}

/** The commitment's own notice documents (the multi-select under "Set for this commitment"). */
export function getNoticeDocumentFields(
  notice: CommitmentNoticeDraft,
  options: SelectListEntry[],
  disabled: boolean
): FieldConfig[] {
  return [
    {
      key: CommitmentFieldKeys.notice_template_ids,
      label: 'Additional notice documents',
      type: FieldTypeEnum.SELECT,
      value: notice.template_ids,
      options,
      selectType: SelectTypeEnum.FILTER,
      mode: 'multiple',
      allowClear: true,
      editable: true,
      disabled,
      placeholder: 'None'
    }
  ]
}

/**
 * Investor section of the New commitment form: the account, then one of its
 * investor entities or "New investor entity". Account and investor are locked
 * when the form was opened for them.
 */
export function getNewCommitmentInvestorFields(
  draft: {
    account: { value: string; label: string } | null
    investor: string | null
  },
  options: { investorOptions: SelectListEntry[]; accountLocked: boolean; investorLocked: boolean }
): FieldConfig[] {
  return [
    {
      key: CommitmentFieldKeys.account,
      label: 'Account',
      type: FieldTypeEnum.COMPANY,
      value: draft.account,
      mode: 'single',
      required: true,
      editable: true,
      disabled: options.accountLocked
    },
    {
      key: CommitmentFieldKeys.investor,
      label: 'Investor',
      type: FieldTypeEnum.SELECT,
      value: draft.investor,
      options: [...options.investorOptions, { value: NEW_INVESTOR_VALUE, label: 'New investor entity' }],
      selectType: SelectTypeEnum.FILTER,
      showSearch: true,
      required: true,
      editable: true,
      disabled: options.investorLocked || draft.account == null,
      placeholder: draft.account == null ? 'Choose the account first' : 'Select investor',
      labelTooltip:
        'The investor entity of the account that holds the commitment. "New investor entity" creates one with the ' +
        'commitment.'
    }
  ]
}

/** The new investor entity's own fields (shown under "New investor entity"). */
export function getNewInvestorFields(
  draft: NewInvestorDraft,
  options: { classificationOptions: SelectListEntry[]; sbicInvestorTypeOptions: SelectListEntry[]; isSbic: boolean }
): FieldConfig[] {
  const fields: FieldConfig[] = [
    {
      key: CommitmentFieldKeys.new_investor_name,
      label: 'Investor name',
      type: FieldTypeEnum.TEXT,
      value: draft.name,
      required: true,
      editable: true
    },
    {
      key: CommitmentFieldKeys.new_investor_legal_name,
      label: 'Legal name',
      type: FieldTypeEnum.TEXT,
      value: draft.legal_name,
      editable: true
    },
    {
      key: CommitmentFieldKeys.new_investor_classification,
      label: 'Classification',
      type: FieldTypeEnum.SELECT,
      value: draft.investor_classification_id,
      options: options.classificationOptions,
      selectType: SelectTypeEnum.FILTER,
      allowClear: true,
      showSearch: true,
      editable: true
    }
  ]
  if (options.isSbic) {
    fields.push({
      key: CommitmentFieldKeys.new_investor_sbic_type,
      label: 'SBIC investor type',
      type: FieldTypeEnum.SELECT,
      value: draft.sbic_investor_type_id,
      options: options.sbicInvestorTypeOptions,
      selectType: SelectTypeEnum.FILTER,
      allowClear: true,
      editable: true,
      labelTooltip: SBIC_INVESTOR_TYPE_HELP
    })
  }
  return fields
}

/** What the commitment is to: one select over the standalone funds and the
 *  consolidated funds (a member fund is not offered). */
export function getTargetFields(
  value: string | null,
  options: { targetOptions: SelectListEntry[]; locked: boolean }
): FieldConfig[] {
  return [
    {
      key: CommitmentFieldKeys.target,
      label: 'Fund',
      type: FieldTypeEnum.SELECT,
      value,
      options: options.targetOptions,
      selectType: SelectTypeEnum.FILTER,
      showSearch: true,
      required: true,
      editable: true,
      disabled: options.locked,
      placeholder: 'Select a fund or a consolidated fund'
    }
  ]
}
